// Generat automat din README.md - Organigramă pentru firma Botez de poveste
import React, { useState } from 'react';
import { Users, Search, Filter, ZoomIn, ZoomOut, Plus, Edit, Eye, Phone, Mail, MapPin, Calendar, Award, ChevronDown, ChevronRight, Building, Star, Crown, Heart, User, Clock } from 'lucide-react';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const OrganigramaDigitala = () => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(['ceo']);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');

  // Structura organizațională pentru Botez de poveste - Un singur angajat care e totul
  const orgData = {
    ceo: {
      id: 'ceo',
      name: 'Margarit Alexandru Florian',
      position: 'Proprietar & Manager General',
      department: 'Management',
      email: 'margarit.alexandru@botezdepoveste.ro',
      phone: '+40 721 456 789',
      avatar: '/avatars/margarit.jpg',
      level: 0,
      directReports: [], // Nu are subordonați - e singur
      startDate: '2023-01-15',
      performance: 98,
      responsibilities: [
        'Management general al afacerii',
        'Design și producție torturi',
        'Relația cu clienții',
        'Marketing și promovare',
        'Gestionare comenzi',
        'Contabilitate și facturare',
        'Aprovizionare materiale',
        'Control calitate produse'
      ],
      workingHours: {
        monday: '09:00-17:00',
        tuesday: '09:00-17:00',
        wednesday: '09:00-17:00', 
        thursday: '09:00-17:00',
        friday: '09:00-17:00',
        saturday: '10:00-14:00',
        sunday: 'Liber'
      }
    }
  };

  const handleEmployeeClick = (employeeId) => {
    setSelectedEmployee(orgData[employeeId]);
    setShowEmployeeModal(true);
  };

  const toggleNodeExpansion = (nodeId) => {
    setExpandedNodes(prev =>
      prev.includes(nodeId)
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 50));
  };

  const EmployeeNode = ({ employee, isExpanded, onToggle, onClick }) => {
    return (
      <div className="flex flex-col items-center">
        <div
          className={`bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all duration-200 min-w-72 ${
            employee.level === 0 ? 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20' : ''
          }`}
          onClick={() => onClick(employee.id)}
          style={{ transform: `scale(${zoomLevel / 100})` }}
        >
          {/* Header cu avatar și nume */}
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl relative">
              {employee.level === 0 && (
                <Crown className="w-6 h-6 absolute -top-2 -right-1 text-yellow-500" />
              )}
              MF
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg text-gray-900 dark:text-white">
                {employee.name}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {employee.position}
              </p>
              <Badge variant="info" className="mt-1">
                {employee.department}
              </Badge>
            </div>
          </div>

          {/* Informații de contact */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
              <Mail className="w-4 h-4" />
              <span>{employee.email}</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
              <Phone className="w-4 h-4" />
              <span>{employee.phone}</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
              <Calendar className="w-4 h-4" />
              <span>Din {employee.startDate}</span>
            </div>
          </div>

          {/* Performance badge */}
          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center space-x-2">
              <Star className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Performance: {employee.performance}%
              </span>
            </div>
            {employee.level === 0 && (
              <div className="flex items-center space-x-1">
                <Crown className="w-4 h-4 text-yellow-500" />
                <span className="text-xs text-yellow-600 font-medium">Proprietar</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Building className="w-8 h-8 text-blue-500" />
            Organigramă Digitală - Botez de poveste
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Structura organizațională completă și informații despre echipă
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleZoomOut}
            className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition-colors"
            title="Zoom Out"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400 px-2">
            {zoomLevel}%
          </span>
          <button
            onClick={handleZoomIn}
            className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition-colors"
            title="Zoom In"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Statistici */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card title="Total Angajați" icon={Users} variant="stats">
          <div className="text-3xl font-bold text-blue-600">1</div>
          <p className="text-sm text-gray-500 mt-1">Proprietar unic</p>
        </Card>

        <Card title="Departamente" icon={Building} variant="stats">
          <div className="text-3xl font-bold text-green-600">1</div>
          <p className="text-sm text-gray-500 mt-1">Management General</p>
        </Card>

        <Card title="Performance Medie" icon={Star} variant="stats">
          <div className="text-3xl font-bold text-yellow-600">98%</div>
          <p className="text-sm text-gray-500 mt-1">Excepțional</p>
        </Card>

        <Card title="Experiență" icon={Calendar} variant="stats">
          <div className="text-3xl font-bold text-purple-600">2+</div>
          <p className="text-sm text-gray-500 mt-1">Ani de activitate</p>
        </Card>
      </div>

      {/* Căutare */}
      <Card>
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Caută angajați..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
        </div>
      </Card>

      {/* Organigramă - Simplă pentru un singur angajat */}
      <Card title="Structura Organizațională">
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <EmployeeNode
              employee={orgData.ceo}
              isExpanded={expandedNodes.includes('ceo')}
              onToggle={() => toggleNodeExpansion('ceo')}
              onClick={handleEmployeeClick}
            />
            
            {/* Mesaj explicativ */}
            <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg max-w-md">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Heart className="w-5 h-5 text-blue-500" />
                <h4 className="font-semibold text-blue-800 dark:text-blue-200">
                  Afacere de Familie
                </h4>
              </div>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Botez de poveste este o afacere mică, personalizată, condusă de 
                <strong> Margarit Alexandru Florian</strong> - proprietarul care se ocupă de toate aspectele 
                pentru a oferi servicii de calitate superioară.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Modal Detalii Angajat */}
      <Modal
        isOpen={showEmployeeModal}
        onClose={() => setShowEmployeeModal(false)}
        title={`Detalii Angajat - ${selectedEmployee?.name}`}
        size="xl"
      >
        {selectedEmployee && (
          <div className="space-y-6">
            {/* Header cu informații de bază */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-6 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-2xl relative">
                    <Crown className="w-6 h-6 absolute -top-2 -right-1 text-yellow-400" />
                    MF
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedEmployee.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-lg">
                      {selectedEmployee.position}
                    </p>
                    <div className="flex items-center space-x-2 mt-2">
                      <Badge variant="info">{selectedEmployee.department}</Badge>
                      <Badge variant="success">Activ</Badge>
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className="flex items-center space-x-1 mb-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    <span className="text-2xl font-bold text-yellow-600">
                      {selectedEmployee.performance}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">Performance</p>
                </div>
              </div>
            </div>

            {/* Informații de contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card title="Informații de Contact" icon={User}>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <span className="text-sm">{selectedEmployee.email}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <span className="text-sm">{selectedEmployee.phone}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-sm">Angajat din {selectedEmployee.startDate}</span>
                  </div>
                </div>
              </Card>

              <Card title="Program de Lucru" icon={Clock}>
                <div className="space-y-2 text-sm">
                  {Object.entries(selectedEmployee.workingHours || {}).map(([day, hours]) => (
                    <div key={day} className="flex justify-between">
                      <span className="font-medium capitalize">
                        {day === 'monday' ? 'Luni' : 
                         day === 'tuesday' ? 'Marți' :
                         day === 'wednesday' ? 'Miercuri' :
                         day === 'thursday' ? 'Joi' :
                         day === 'friday' ? 'Vineri' :
                         day === 'saturday' ? 'Sâmbătă' :
                         'Duminică'}:
                      </span>
                      <span className={hours === 'Liber' ? 'text-green-600' : 'text-gray-700 dark:text-gray-300'}>
                        {hours}
                      </span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Responsabilități */}
            <Card title="Responsabilități Principale" icon={Award}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {selectedEmployee.responsibilities?.map((responsibility, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {responsibility}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Footer */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                onClick={() => setShowEmployeeModal(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Închide
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrganigramaDigitala;